import { createSlice } from '@reduxjs/toolkit';

import type { PortalRole } from '@/containers/team/TeamMemberType';
import type { RootState } from '@/redux/store';

export interface PortalAccessInitialState {
  role: PortalRole | null;
  permissions: string[];
}

export const initialState: PortalAccessInitialState = {
  role: null,
  permissions: [],
};

const slice = createSlice({
  name: 'portalAccessSlice',
  initialState,
  reducers: {
    // Role and permissions always come from the same member-list lookup
    // (see deriveCurrentUserAccess in api/teamApiHooks.ts) and should never
    // be set independently of each other, so this is one action instead of
    // two — avoids either field going stale relative to the other.
    setPortalAccess(
      state,
      action: {
        payload: { role: PortalRole | null; permissions: string[] };
      }
    ) {
      state.role = action.payload.role;
      state.permissions = action.payload.permissions;
    },
    resetPortalAccess(state) {
      state.role = null;
      state.permissions = [];
    },
  },
});

export const { setPortalAccess, resetPortalAccess } = slice.actions;

// ---- Selectors -------------------------------------------------------
// Import these + `useSelector` from '@/redux/store' anywhere in the app
// instead of inlining `state.portalAccessSlice.*` by hand — see this
// slice's usage notes in rbac-testing/NOTES.md for examples.

export const selectPortalRole = (state: RootState): PortalRole | null =>
  state.portalAccessSlice.role;

export const selectPortalPermissions = (state: RootState): string[] =>
  state.portalAccessSlice.permissions;

// Curried so it can be used directly as `useSelector(selectHasPortalPermission('portal.forms.edit'))`.
export const selectHasPortalPermission =
  (permissionKey: string) =>
  (state: RootState): boolean =>
    state.portalAccessSlice.permissions.includes(permissionKey);

export default slice.reducer;
